import useI18n from "@I18n/hooks/useI18n";
import { useDataQuery } from "@Libs/DataLayer/hooks/useDataQuery";
import { GOOGLE_MAPS_BROWSER_API_KEY, getCurrentCountry } from "@config";
import { missingAddressParts } from "../Components/Questions/AddressQuestion/addressCompleteness";
import { geocodeAddress } from "../Components/Questions/AddressQuestion/geocodeAddress";
import { useGoogleMapsScript } from "../Components/Questions/AddressQuestion/useGoogleMapsScript";
import { useTaskCreationFlow } from "../Contexts/TaskCreationFlow";
import { ISuggestedAddress } from "../types";
import { findSuggestedAddress } from "../utils/suggestedAddressValues";

export const SUGGESTED_ADDRESS_QUERY_KEY = "taskCreationSuggestedAddress";

/**
 * The address of the client's last task, once Google has resolved it to a
 * doorstep — `undefined` until then, and for good if it does not. Tasks only
 * store the address string, and older ones were saved without the completeness
 * check, so a bare "Bruxelles" must not be prefilled and trusted.
 */
export const useSuggestedAddress = (): ISuggestedAddress | undefined => {
  const { draft } = useTaskCreationFlow();
  const { locale } = useI18n();
  const suggestion = findSuggestedAddress(draft?.answers);
  const { isLoaded } = useGoogleMapsScript(
    suggestion ? GOOGLE_MAPS_BROWSER_API_KEY : undefined,
    { language: locale }
  );
  const country = getCurrentCountry();

  const { data: isComplete } = useDataQuery(
    [SUGGESTED_ADDRESS_QUERY_KEY, suggestion?.formatted_address, country],
    async () =>
      missingAddressParts(
        await geocodeAddress(suggestion?.formatted_address ?? "", country)
      ).length === 0,
    { enabled: isLoaded && !!suggestion, staleTime: Infinity, retry: false }
  );

  return isComplete ? suggestion : undefined;
};
